import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import CreateWarriorModal from '../components/heroes/CreateWarriorModal';
import { useJWTAuth } from '../auth/JWTAuthContext';
import AuthGuard from '../components/AuthGuard';
import { socketService } from '../services/SocketService';
import AccountLinkingSection from '../components/AccountLinkingSection';
import { useToast } from '../contexts/ToastContext';

const Heroes = () => {
  const [heroes, setHeroes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userId, setUserId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [showFrequency, setShowFrequency] = useState(false);
  const { user, getAuthHeaders, isAuthenticated, token } = useJWTAuth();
  const { success: showSuccessToast, error: showErrorToast } = useToast();

  // Connect to socket when component mounts and user is authenticated
  useEffect(() => {
    if (isAuthenticated() && token && !socketService.isSocketConnected()) {
      console.log('🔌 [Heroes] Connecting to socket server...');
      socketService.connect(token);
    }
    
    return () => {
      // Disconnect socket when leaving Heroes page
      if (socketService.isSocketConnected()) {
        console.log('🔌 [Heroes] Disconnecting from socket server...');
        socketService.disconnect();
      }
    };
  }, [isAuthenticated, token]);

  const fetchUserHeroes = useCallback(async () => {
    // Don't fetch if user is not loaded yet
    if (!user?.id) {
      setLoading(false);
      return;
    }

    try {
      const currentUserId = user.id;
      setUserId(currentUserId);

      const response = await fetch(`/api/heroes/user/${currentUserId}`);
      const data = await response.json();

      if (data.success) {
        // Sort heroes by total XP (highest first)
        const sortedHeroes = data.data.sort((a, b) => b.total_xp - a.total_xp);
        setHeroes(sortedHeroes);
      } else {
        setError(data.error);
      }
    } catch (error) {
      console.error('Error fetching heroes:', error);
      setError('Failed to load Heroes');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchUserHeroes();
  }, [user, fetchUserHeroes]);

  // Set up socket listener for account claim events
  useEffect(() => {
    const handleAccountClaimed = (data) => {
      if (data.success) {
        const platformName = data.platformDisplay || data.platform;
        const heroes = data.heroes || [];
        const heroCount = heroes.length || data.heroesTransferred || 0;
        const heroText = heroCount === 1 ? 'hero' : 'heroes';
        
        // Build hero names list
        let heroNamesText = '';
        if (heroes.length > 0) {
          const heroNames = heroes.map(h => h.name).join(', ');
          heroNamesText = ` (${heroNames})`;
        }
        
        showSuccessToast(
          `Successfully claimed your ${platformName} account! ${heroCount} ${heroText} transferred${heroNamesText}.`,
          60000 // 60 seconds duration
        );
        // Refresh heroes list
        fetchUserHeroes();
        // Trigger account linking refresh by dispatching custom event
        window.dispatchEvent(new CustomEvent('account-claimed', { detail: { platform: data.platform } }));
      } else {
        showErrorToast(data.error || 'Failed to claim account');
      }
    };

    socketService.onAccountClaimed(handleAccountClaimed);
  }, [fetchUserHeroes, showSuccessToast, showErrorToast]);

  const handleSetActive = (heroId) => {
    setHeroes(prev => prev.map(hero => ({
      ...hero,
      is_active: hero.id === heroId
    })));
  };


  const handleCreateWarrior = () => setCreateOpen(true);

  const handleCreated = (hero) => {
    setHeroes(prev => [hero, ...prev]);
  };

  const handleFrequencyUpdate = async (heroId, frequency) => {
    try {
      // Check if user is authenticated
      if (!isAuthenticated()) {
        console.error('User not authenticated');
        alert('Please log in to update hero frequency');
        return;
      }

      const response = await fetch(`/api/heroes/${heroId}/frequency`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({ frequency })
      });

      const data = await response.json();
      
      if (data.success) {
        setHeroes(prev => prev.map(hero => 
          hero.id === heroId ? { ...hero, frequency } : hero
        ));
        console.log('✅ Frequency updated successfully');
      } else {
        console.error('Failed to update frequency:', data.error);
        alert(`Failed to update frequency: ${data.error}`);
      }
    } catch (error) {
      console.error('Error updating frequency:', error);
      alert('Error updating frequency. Please try again.');
    }
  };

  const getFrequencyLabel = (frequency) => {
    switch (frequency) {
      case 0: return 'Never';
      case 3: return 'Seldom';
      case 6: return 'Often';
      case 11: return 'Frequent';
      default: return 'Often';
    }
  };

  const getFrequencyColor = (frequency) => {
    switch (frequency) {
      case 0: return 'bg-red-500 hover:bg-red-600';
      case 3: return 'bg-orange-500 hover:bg-orange-600';
      case 6: return 'bg-yellow-500 hover:bg-yellow-600';
      case 11: return 'bg-green-500 hover:bg-green-600';
      default: return 'bg-yellow-500 hover:bg-yellow-600';
    }
  };

  const getFrequencyColorDimmed = (frequency) => {
    switch (frequency) {
      case 0: return 'bg-red-500/10 hover:bg-red-500/30 border border-red-500/30';
      case 3: return 'bg-orange-500/10 hover:bg-orange-500/30 border border-orange-500/30';
      case 6: return 'bg-yellow-500/10 hover:bg-yellow-500/30 border border-yellow-500/30';
      case 11: return 'bg-green-500/10 hover:bg-green-500/30 border border-green-500/30';
      default: return 'bg-yellow-500/10 hover:bg-yellow-500/30 border border-yellow-500/30';
    }
  };

  const getFrequencyCapColor = (frequency) => {
    switch (frequency) {
      case 0: return 'bg-red-500';
      case 3: return 'bg-orange-500';
      case 6: return 'bg-yellow-500';
      case 11: return 'bg-green-500';
      default: return 'bg-yellow-500';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading Heroes...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-red-400 text-xl">Error: {error}</div>
      </div>
    );
  }

  return (
    <AuthGuard>
      <div className="min-h-screen bg-gray-900 py-8">
        <div className="max-w-6xl mx-auto px-4">
        {/* Header with Create Button and View Toggle */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Heroes</h1>
            <p className="text-gray-400">
              Manage your battle avatars, customize attributes, and set AI strategies.
            </p>
          </div>
          {heroes.length > 0 && (
            <div className="mt-4 sm:mt-0">
              <button
                onClick={handleCreateWarrior}
                className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
              >
                <span>⚔️</span>
                New Hero
              </button>
            </div>
          )}
        </div>

        {/* Main Content */}
        {heroes.length > 0 ? (
          <>
            {/* Heroes Display */}
            <div className="space-y-2 mb-6">
              {heroes.map(hero => (
                <Link
                  key={hero.id}
                  to={`/hero/${hero.id}`}
                  className={`block rounded-lg p-4 border-2 transition-all duration-200 cursor-pointer relative ${
                    hero.is_active ? 'border-blue-500 bg-blue-900/20' : 'border-gray-600 hover:border-gray-500 bg-gray-800'
                  }`}
                >
                  {/* Frequency indicator cap */}
                  <div className={`absolute left-0 top-0 bottom-0 w-2 rounded-l-lg ${getFrequencyCapColor(hero.frequency !== null && hero.frequency !== undefined ? hero.frequency : 6)}`}></div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded bg-gray-700 border border-gray-600 overflow-hidden flex items-center justify-center">
                        {hero.image_url ? (
                          <img src={hero.image_url} alt="Hero" className="h-full w-full object-contain" />
                        ) : (
                          <span className="text-sm text-gray-400">m/t</span>
                        )}
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-white">{hero.name}</h3>
                        {!showFrequency && (
                          <p className="text-sm text-gray-400">
                            Level {hero.level} • {hero.unspent_xp.toLocaleString()} unspent XP • {getFrequencyLabel(hero.frequency !== null && hero.frequency !== undefined ? hero.frequency : 6)} frequency
                          </p>
                        )}
                        {showFrequency && (
                          <p className="text-sm text-gray-400">
                            Level {hero.level}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-6">
                      {/* Frequency Buttons - Only show when toggle is on */}
                      {showFrequency && (
                        <div className="flex gap-1">
                          {[0, 3, 6, 11].map((freq) => (
                            <button
                              key={freq}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleFrequencyUpdate(hero.id, freq);
                              }}
                              className={`w-8 h-8 rounded text-xs font-bold text-white transition-colors ${
                                (hero.frequency !== null && hero.frequency !== undefined ? hero.frequency : 6) === freq
                                  ? getFrequencyColor(freq)
                                  : getFrequencyColorDimmed(freq)
                              }`}
                              title={getFrequencyLabel(freq)}
                            >
                              {freq === 0 ? '🚫' : freq === 3 ? 'S' : freq === 6 ? 'O' : 'F'}
                            </button>
                          ))}
                        </div>
                      )}

                      {!showFrequency && (
                        <div className="text-center">
                          <div className="text-sm text-gray-400">Total XP</div>
                          <div className="text-lg font-bold text-yellow-400">
                            {hero.total_xp.toLocaleString()}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {/* Frequency Legend - Show when frequency is visible */}
            {showFrequency && (
              <div className="bg-gray-800 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-white">Battle Frequency Settings</h3>
                  <button
                    onClick={() => setShowFrequency(false)}
                    className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                  >
                    Hide
                  </button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-red-500 rounded text-xs font-bold text-white flex items-center justify-center">🚫</div>
                    <div>
                      <div className="text-white font-medium">Never (0)</div>
                      <div className="text-gray-400 text-sm">DO NOT USE</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-orange-500 rounded text-xs font-bold text-white flex items-center justify-center">S</div>
                    <div>
                      <div className="text-white font-medium">Seldom (3)</div>
                      <div className="text-gray-400 text-sm">Low priority</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-yellow-500 rounded text-xs font-bold text-white flex items-center justify-center">O</div>
                    <div>
                      <div className="text-white font-medium">Often (6)</div>
                      <div className="text-gray-400 text-sm">Default setting</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-green-500 rounded text-xs font-bold text-white flex items-center justify-center">F</div>
                    <div>
                      <div className="text-white font-medium">Frequent (11)</div>
                      <div className="text-gray-400 text-sm">High priority</div>
                    </div>
                  </div>
                </div>
                <p className="text-gray-400 text-sm mt-3">
                  Click the colored buttons next to each hero to set their battle selection frequency. 
                  Heroes with higher frequency values are more likely to be selected for battles.
                </p>
              </div>
            )}

            {/* Frequency Cap Legend - Show when frequency is hidden */}
            {!showFrequency && (
              <div className="bg-gray-800 rounded-lg p-3 mb-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-white">🎯 Battle Frequency Indicators</h3>
                  <button
                    onClick={() => setShowFrequency(true)}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm font-medium transition-colors"
                  >
                    Adjust
                  </button>
                </div>
                <div className="flex items-center gap-4 text-xs">
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 bg-red-500 rounded-l"></div>
                    <span className="text-gray-400">Never</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 bg-orange-500 rounded-l"></div>
                    <span className="text-gray-400">Seldom</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 bg-yellow-500 rounded-l"></div>
                    <span className="text-gray-400">Often</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 bg-green-500 rounded-l"></div>
                    <span className="text-gray-400">Frequent</span>
                  </div>
                </div>
              </div>
            )}

            {/* Quick Stats */}
            <div className="bg-gray-800 rounded-lg p-6 mb-8">
              <h2 className="text-xl font-bold text-white mb-4">Your Heroes</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-blue-400">{heroes.length}</div>
                  <div className="text-sm text-gray-400">Total Heroes</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-green-400">
                    {heroes.filter(w => w.is_active).length}
                  </div>
                  <div className="text-sm text-gray-400">Active</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-yellow-400">
                    {Math.max(...heroes.map(w => w.level))}
                  </div>
                  <div className="text-sm text-gray-400">Highest Level</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-purple-400">
                    {heroes.reduce((sum, w) => sum + w.total_xp, 0).toLocaleString()}
                  </div>
                  <div className="text-sm text-gray-400">Total XP</div>
                </div>
              </div>
            </div>
          </>
        ) : (
          /* Empty State */
          <div className="text-center py-16">
            <div className="text-6xl mb-6">⚔️</div>
            <h2 className="text-2xl font-bold text-white mb-4">No Heroes Yet</h2>
            <p className="text-gray-400 mb-8 max-w-md mx-auto">
              Create your first Hero to start earning XP, leveling up, and competing in seasonal battles.
            </p>
            <button
              onClick={handleCreateWarrior}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8 py-4 rounded-lg font-medium transition-all duration-200 transform hover:scale-105 shadow-lg"
            >
              Create Your First Hero
            </button>

            {/* Feature Highlights */}
            <div className="mt-12 max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
              <div className="bg-gray-800/60 rounded-lg p-5">
                <div className="text-white font-semibold mb-1">🎯 Attributes</div>
                <div className="text-gray-300 text-sm">Six core stats (Strength, Endurance, Coordination, Quickness, Focus, Self) shape combat performance.</div>
              </div>
              <div className="bg-gray-800/60 rounded-lg p-5">
                <div className="text-white font-semibold mb-1">🧠 AI Strategy</div>
                <div className="text-gray-300 text-sm">Tune aggression vs defense, loot vs heal, and edge vs center with weighted sliders.</div>
              </div>
              <div className="bg-gray-800/60 rounded-lg p-5">
                <div className="text-white font-semibold mb-1">⭐ Earn XP</div>
                <div className="text-gray-300 text-sm">Gain XP from placement and eliminations. Spend XP to grow attributes with a smooth cost curve.</div>
              </div>
              <div className="bg-gray-800/60 rounded-lg p-5">
                <div className="text-white font-semibold mb-1">🏆 Seasons & Leaderboards</div>
                <div className="text-gray-300 text-sm">Compete each season and climb XP, wins, and eliminations leaderboards.</div>
              </div>
            </div>
          </div>
        )}

        {/* Account Linking Section */}
        {user && <AccountLinkingSection />}

        {/* Help Section - Only show if user has heroes */}
        {heroes.length > 0 && (
          <>
          <div className="bg-gray-800 rounded-lg p-6 mb-6">
            <h2 className="text-xl font-bold text-white mb-4">Quick Guide</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-gray-300">
              <div>
                <h3 className="font-semibold text-white mb-2">🎯 Attributes</h3>
                <p className="text-sm">
                  Six core stats (Strength, Endurance, Coordination, Quickness, Focus, Self) that affect battle performance.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-white mb-2">🧠 AI Strategy</h3>
                <p className="text-sm">
                  Configure combat style, resource priority, and positioning preferences to customize your hero's behavior.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-white mb-2">⭐ XP System</h3>
                <p className="text-sm">
                  Earn XP from battles based on placement and eliminations. Spend XP manually to increase attributes.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-white mb-2">🏆 Competition</h3>
                <p className="text-sm">
                  Compete in seasonal leaderboards and track your progress against other heroes.
                </p>
              </div>
            </div>
          </div>

           <div className="bg-gray-800 rounded-lg p-6 mb-6">
             <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
               <div className="flex-1">
                 <h2 className="text-xl font-bold text-white mb-4">Battle Simulator Guide</h2>
                 <p className="text-gray-300 mb-4">
                   Test your heroes' combat effectiveness! Simulate battles between any two heroes with different strategies, 
                   equipment, and aggression levels. Watch real-time combat or analyze turn-by-turn results with detailed 
                   damage calculations, hit rates, and performance metrics. Numbers Nerds only!
                 </p>
                 <div className="flex flex-wrap gap-2 text-sm text-gray-400">
                   <span className="bg-gray-700 px-2 py-1 rounded">Real-time Combat</span>
                   <span className="bg-gray-700 px-2 py-1 rounded">Strategy Testing</span>
                   <span className="bg-gray-700 px-2 py-1 rounded">Performance Analytics</span>
                   <span className="bg-gray-700 px-2 py-1 rounded">Damage Calculations</span>
                 </div>
               </div>
               <div className="flex-shrink-0 lg:ml-6">
                 <Link
                   to="/battle-simulator"
                   className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors duration-200 flex items-center gap-2 w-full lg:w-auto lg:min-w-[200px] justify-center"
                 >
                   <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                   </svg>
                   Launch Battle Simulator
                 </Link>
               </div>
             </div>
           </div>
          </>
        )}

        {/* Debug Authentication Status */}
        <div className="bg-gray-800 rounded-lg p-4 mb-6">
          <h3 className="text-lg font-bold text-white mb-3">🔧 Authentication Status</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-gray-400 mb-2">JWT Authentication:</div>
              <div className="text-white">
                {isAuthenticated() ? `✅ Logged in as ${user?.username} (ID: ${user?.id})` : '❌ Not authenticated'}
              </div>
            </div>
            <div>
              <div className="text-gray-400 mb-2">Current User ID:</div>
              <div className="text-white">{userId || 'Loading...'}</div>
            </div>
          </div>
        </div>
      </div>
        {/* Modal Mount */}
        <CreateWarriorModal
          isOpen={createOpen}
          onClose={() => setCreateOpen(false)}
          onCreated={handleCreated}
        />
      </div>
    </AuthGuard>
  );
};

export default Heroes;

